import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import { Plus, FileText, Check } from "lucide-react";
import { toast } from "sonner";

export default function TeacherDashboard() {
  const [a, setA] = useState({ title: "", description: "", due_date: "" });
  const [n, setN] = useState({ title: "", content: "", test_type: "IELTS_ACADEMIC", section: "writing" });
  const [assignments, setAssignments] = useState([]);
  const [openA, setOpenA] = useState(null);
  const [subs, setSubs] = useState([]);
  const [grading, setGrading] = useState({});

  const loadA = async () => {
    const r = await api.get("/assignments");
    setAssignments(r.data);
  };
  useEffect(() => { loadA(); }, []);

  const createA = async () => {
    try {
      await api.post("/assignments", a);
      toast.success("Assignment created");
      setA({ title: "", description: "", due_date: "" });
      loadA();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const createN = async () => {
    try {
      await api.post("/notes", n);
      toast.success("Note posted");
      setN({ ...n, title: "", content: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAssignment = async (id) => {
    setOpenA(id);
    const r = await api.get(`/assignments/${id}/submissions`);
    setSubs(r.data);
  };

  const grade = async (sid) => {
    const g = grading[sid] || {};
    if (!g.grade) return toast.error("Enter a grade");
    try {
      await api.post("/assignments/grade", { submission_id: sid, grade: g.grade, feedback: g.feedback || "" });
      toast.success("Graded");
      openAssignment(openA);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardLayout title="Teacher Dashboard">
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="font-display text-xl mb-4">Create assignment</div>
          <input data-testid="ta-title" placeholder="Title" value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5 mb-3" />
          <textarea data-testid="ta-desc" placeholder="Description" rows={3} value={a.description} onChange={(e) => setA({ ...a, description: e.target.value })} className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5 mb-3" />
          <input data-testid="ta-due" type="date" value={a.due_date} onChange={(e) => setA({ ...a, due_date: e.target.value })} className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5 mb-3" />
          <button onClick={createA} data-testid="ta-create" className="bg-[#F77F00] text-white rounded-xl px-5 py-2.5 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Create</button>
        </div>
        <div className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="font-display text-xl mb-4">Upload note</div>
          <input data-testid="tn-title" placeholder="Title" value={n.title} onChange={(e) => setN({ ...n, title: e.target.value })} className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5 mb-3" />
          <textarea data-testid="tn-content" placeholder="Note content" rows={3} value={n.content} onChange={(e) => setN({ ...n, content: e.target.value })} className="w-full bg-[#F6F8FB]/50 border border-slate-200 rounded-xl px-4 py-2.5 mb-3" />
          <button onClick={createN} data-testid="tn-create" className="bg-[#F77F00] text-white rounded-xl px-5 py-2.5 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Post</button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-3xl p-6">
        <div className="font-display text-xl mb-4">Assignments & submissions</div>
        <div className="grid lg:grid-cols-3 gap-3">
          {assignments.map((x) => (
            <button key={x.id} onClick={() => openAssignment(x.id)} data-testid={`assignment-${x.id}`}
              className={`text-left rounded-xl p-4 border ${openA === x.id ? "border-[#0047FF] bg-[#FFF4E5]" : "border-slate-200 hover:border-slate-300"}`}>
              <FileText className="w-4 h-4 text-[#F77F00] mb-2" />
              <div className="font-medium">{x.title}</div>
              <div className="text-xs text-slate-400 mt-1">Due: {x.due_date || "—"}</div>
            </button>
          ))}
          {!assignments.length && <div className="text-slate-400">No assignments yet.</div>}
        </div>

        {openA && (
          <div className="mt-6 border-t border-slate-200 pt-6">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Submissions ({subs.length})</div>
            <div className="space-y-3">
              {subs.map((s) => (
                <div key={s.id} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{s.user_name}</div>
                    <div className="text-xs text-slate-400">{s.submitted_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="text-sm text-slate-600 whitespace-pre-wrap mb-3">{s.content}</div>
                  {s.grade ? (
                    <div className="text-xs text-[#16A34A] inline-flex items-center gap-1"><Check className="w-3 h-3" /> Grade: <span className="font-mono">{s.grade}</span></div>
                  ) : (
                    <div className="flex flex-wrap gap-2 items-center">
                      <input data-testid={`grade-${s.id}`} placeholder="Grade (e.g. 7.5)" value={grading[s.id]?.grade || ""}
                        onChange={(e) => setGrading({ ...grading, [s.id]: { ...grading[s.id], grade: e.target.value } })}
                        className="bg-[#F6F8FB]/50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-32" />
                      <input data-testid={`feedback-${s.id}`} placeholder="Feedback (optional)" value={grading[s.id]?.feedback || ""}
                        onChange={(e) => setGrading({ ...grading, [s.id]: { ...grading[s.id], feedback: e.target.value } })}
                        className="bg-[#F6F8FB]/50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[200px]" />
                      <button onClick={() => grade(s.id)} data-testid={`grade-btn-${s.id}`} className="px-4 py-1.5 rounded-lg bg-[#0047FF] text-sm">Grade</button>
                    </div>
                  )}
                </div>
              ))}
              {!subs.length && <div className="text-slate-400 text-sm">No submissions yet.</div>}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
